import { useId, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import {
  Button,
  CloseIcon,
  confirmDialog,
  Dialog,
  DocumentIcon,
  InputTextarea,
  UploadIcon,
  useGlobalToast,
} from '@repo/atomic-components';
import { classNames as cn } from 'primereact/utils';
import { useBulkDeleteMattersMutation } from '@/api';
import { BULK_DELETE_MATTERS_CONFIRM } from '@/constants';
import { DeleteConfirmationDialog } from '@/ui/components/DeleteConfirmationDialog';
import { downloadBlob } from '@/utils/downloadBlob';
import { extractFileListFromDataTransfer } from '@/utils/extractFileListFromDataTransfer';
import { SegmentedControl } from '../SegmentedControl';
import {
  BULK_DELETE_MATTERS_COLUMN,
  BULK_DELETE_MATTERS_TEMPLATE_CSV,
  BULK_DELETE_MATTERS_TEMPLATE_FILE_NAME,
  parsePastedCaseReferences,
  validateBulkDeleteMattersCsv,
} from './validateBulkDeleteMattersCsv';

interface BulkDeleteMattersModalProps {
  visible: boolean;
  onHide: () => void;
}

type InputMode = 'paste' | 'upload';

const INPUT_MODES = [
  { value: 'paste', label: 'Paste references' },
  { value: 'upload', label: 'Upload CSV' },
] as const satisfies readonly { value: InputMode; label: string }[];

const GENERIC_DELETE_ERROR = 'The matters could not be deleted. Please check the case references and try again.';

const PASTED_REFERENCES_MAX_HEIGHT = 168;

const SECTION_HEADING_CLASS = 'text-theme-text-grey text-[0.6875rem] font-semibold tracking-[0.5px] uppercase';

const pluralMatters = (count: number) => `${count} matter${count === 1 ? '' : 's'}`;

/** Global-admin tool that deletes matters by case reference, pasted or uploaded as a CSV, after a confirmation. */
export const BulkDeleteMattersModal = ({ visible, onHide }: BulkDeleteMattersModalProps) => {
  const globalToastRef = useGlobalToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const modeLabelId = useId();
  const pasteInputId = useId();
  const statusId = useId();

  const [mode, setMode] = useState<InputMode>('paste');
  const [pastedText, setPastedText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [bulkDeleteMatters, { isLoading: isSubmitting }] = useBulkDeleteMattersMutation();

  const pasteValidation = useMemo(
    () => (pastedText.trim() ? parsePastedCaseReferences(pastedText) : null),
    [pastedText],
  );
  const fileValidation = useMemo(
    () => (fileContent === null ? null : validateBulkDeleteMattersCsv(fileContent)),
    [fileContent],
  );
  const validation = mode === 'paste' ? pasteValidation : fileValidation;

  const errorMessage =
    submitError ?? (mode === 'upload' ? fileError : null) ?? (validation?.isValid === false ? validation.error : null);
  const rowErrors = validation?.isValid === false ? validation.rowErrors : [];
  const duplicates = mode === 'paste' ? (pasteValidation?.duplicates ?? []) : [];

  const resetState = () => {
    setMode('paste');
    setPastedText('');
    setFileName(null);
    setFileContent(null);
    setFileError(null);
    setIsDragging(false);
    setSubmitError(null);
  };

  const handleHide = () => {
    resetState();
    onHide();
  };

  const handleModeChange = (nextMode: InputMode) => {
    setMode(nextMode);
    setSubmitError(null);
  };

  const handlePastedTextChange = (value: string) => {
    setPastedText(value);
    setSubmitError(null);
  };

  const handleTemplateDownload = () => {
    downloadBlob(
      new Blob([BULK_DELETE_MATTERS_TEMPLATE_CSV], { type: 'text/csv;charset=utf-8' }),
      BULK_DELETE_MATTERS_TEMPLATE_FILE_NAME,
    );
  };

  const handleFileSelected = async (file: File | undefined) => {
    if (!file) {
      return;
    }

    setFileName(file.name);
    setSubmitError(null);

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setFileContent(null);
      setFileError('Only CSV files can be uploaded.');
      return;
    }

    setFileContent(await file.text());
    setFileError(null);
  };

  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    void handleFileSelected(event.target.files?.[0]);
    // Allow re-selecting the same file after fixing it on disk.
    event.target.value = '';
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    void handleFileSelected(extractFileListFromDataTransfer(event.dataTransfer)?.[0]);
  };

  const handleDelete = async () => {
    if (!validation?.isValid) {
      return;
    }

    const confirmed = await confirmDialog(
      {
        id: BULK_DELETE_MATTERS_CONFIRM,
        title: `Delete ${pluralMatters(validation.rowCount)}?`,
        description: "All of their files and chats will be removed. This can't be undone.",
      },
      { forceClosePrev: true },
    );

    if (!confirmed) {
      return;
    }

    try {
      const { matterIds } = await bulkDeleteMatters({ csvString: validation.csv }).unwrap();

      globalToastRef.current?.show({
        closable: true,
        severity: 'success',
        detail: `${pluralMatters(matterIds.length)} deleted.`,
      });
      handleHide();
    } catch (error) {
      const detail = (error as { data?: { detail?: unknown } })?.data?.detail;
      // Keep what was entered, so the admin can correct it and try again.
      setSubmitError(typeof detail === 'string' ? detail : GENERIC_DELETE_ERROR);
    }
  };

  return (
    <>
      <Dialog
        visible={visible}
        onHide={handleHide}
        modal
        draggable={false}
        closable={false}
        header={
          <div className="text-theme-black flex w-full items-center justify-between font-semibold">
            <span className="text-[1.25rem] leading-7.5">Bulk delete matters</span>
            <Button
              type="button"
              onClick={handleHide}
              outlined
              className="text-theme-black hover:bg-theme-background-grey hover:text-theme-black flex h-8 w-8 items-center justify-center rounded border-none transition-colors"
              aria-label="Close"
            >
              <CloseIcon className="h-[16px] w-[16px]" />
            </Button>
          </div>
        }
        className="!w-[min(39rem,calc(100vw-48px))]"
        pt={{
          // A column, so the content scrolls between header and footer once the row problems make it tall.
          root: { className: 'rounded-lg dialog-shadow flex flex-col' },
          header: {
            className:
              'cursor-default !rounded-t-lg bg-theme-white text-theme-black border-theme-border-grey border-b px-6 py-4',
          },
          headerTitle: { className: '!m-0 !w-full' },
          content: { className: 'bg-theme-white px-6 py-6' },
          footer: { className: 'bg-theme-white !rounded-b-lg border-theme-border-grey border-t px-6 py-4' },
          closeButton: { className: 'hidden' },
        }}
        footer={
          <div className="flex items-center justify-end gap-3">
            <Button
              outlined
              onClick={handleHide}
              className="!text-theme-black !border-theme-border-grey hover:bg-theme-background-grey! !bg-theme-white flex h-8 items-center justify-center px-4 text-sm"
            >
              Cancel
            </Button>
            <Button
              disabled={!validation?.isValid || isSubmitting}
              loading={isSubmitting}
              onClick={() => void handleDelete()}
              className="bg-theme-dark-green hover:bg-theme-dark-green text-theme-white flex h-8 items-center justify-center px-4 text-sm"
            >
              Delete matters
            </Button>
          </div>
        }
      >
        <div className="bg-theme-background-red border-theme-border-red text-theme-black flex items-start gap-3 rounded-lg border p-4 text-xs leading-5">
          <i className="pi pi-exclamation-triangle text-theme-text-red mt-[2px] text-sm" />
          <p>
            <span className="font-semibold">Deletion is permanent.</span> Matters, with their files and chats, are
            removed for everyone in your organisation. If any case reference isn&apos;t found, nothing is deleted.
          </p>
        </div>

        <section className="mt-6 flex flex-col gap-2">
          <h3 id={modeLabelId} className={SECTION_HEADING_CLASS}>
            Case references
          </h3>
          <SegmentedControl labelId={modeLabelId} options={INPUT_MODES} value={mode} onChange={handleModeChange} />
        </section>

        {mode === 'paste' ? (
          <section className="mt-4 flex flex-col gap-1">
            <label htmlFor={pasteInputId} className="text-theme-black text-sm font-semibold">
              Case references to delete
            </label>
            <span id={`${pasteInputId}-hint`} className="text-theme-text-grey text-xs">
              One per line, or separated by commas. You can paste a column straight from Excel.
            </span>
            <InputTextarea
              id={pasteInputId}
              aria-describedby={`${pasteInputId}-hint ${statusId}`}
              value={pastedText}
              onChange={(event) => handlePastedTextChange(event.target.value)}
              autoResize
              rows={4}
              spellCheck={false}
              style={{ maxHeight: PASTED_REFERENCES_MAX_HEIGHT }}
              className={cn(
                '!text-theme-black !bg-theme-white mt-1 w-full !resize-none !rounded-lg border font-mono !text-sm',
                errorMessage ? '!border-theme-border-red' : '!border-theme-border-grey',
              )}
            />
          </section>
        ) : (
          <>
            <section className="mt-4 flex flex-col gap-2">
              <h3 className={SECTION_HEADING_CLASS}>1. Download template</h3>
              <div className="border-theme-border-grey bg-theme-background-grey flex items-center justify-between gap-3 rounded-lg border px-4 py-3">
                <span className="text-theme-black flex items-center gap-3 text-sm">
                  <DocumentIcon className="h-[16px] w-[16px] shrink-0" width={14} height={16} />
                  {BULK_DELETE_MATTERS_TEMPLATE_FILE_NAME}
                </span>
                <Button
                  text
                  onClick={handleTemplateDownload}
                  className="!text-theme-dark-green h-auto p-0 text-sm !font-semibold hover:!bg-transparent hover:underline"
                >
                  Download
                </Button>
              </div>
            </section>

            <section className="mt-6 flex flex-col gap-2">
              <h3 className={SECTION_HEADING_CLASS}>2. Upload completed CSV</h3>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={handleFileInputChange}
              />
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={cn('rounded-lg border border-dashed transition-colors', {
                  'border-theme-dark-green bg-theme-highlight-green': isDragging,
                  'border-theme-border-red': !isDragging && !!errorMessage,
                  'border-theme-mid-grey': !isDragging && !errorMessage,
                })}
              >
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  aria-describedby={fileName ? statusId : undefined}
                  className="flex h-31 w-full cursor-pointer flex-col items-center justify-center gap-2.5 px-4 text-center"
                >
                  <UploadIcon className="text-theme-dark-green h-[20px] w-[19px]" />
                  {fileName ? (
                    <span className="text-theme-black text-sm font-semibold break-all">{fileName}</span>
                  ) : (
                    <span className="text-theme-black text-sm">
                      <span className="text-theme-dark-green font-semibold">Click to upload</span> or drag and drop
                    </span>
                  )}
                  <span className="text-theme-text-grey text-xs">
                    CSV files only, with a <span className="font-semibold">{BULK_DELETE_MATTERS_COLUMN}</span> column
                  </span>
                </button>
              </div>
            </section>
          </>
        )}

        {/* Kept in the layout while empty: some screen readers miss a live region that starts hidden. */}
        <div id={statusId} aria-live="polite" className="mt-2 text-xs empty:mt-0">
          {errorMessage && (
            <p role={submitError ? 'alert' : undefined} className="text-theme-text-red">
              {errorMessage}
            </p>
          )}
          {validation?.isValid && !submitError && (
            <p className="text-theme-text-grey">{pluralMatters(validation.rowCount)} ready to delete.</p>
          )}
          {duplicates.length > 0 && (
            <p className="text-theme-text-grey">Listed more than once, deleted once: {duplicates.join(', ')}.</p>
          )}
        </div>
        {rowErrors.length > 0 && (
          <ul
            tabIndex={0}
            aria-label="Rows to fix"
            className="border-theme-border-grey text-theme-black mt-2 flex max-h-[168px] flex-col gap-1.5 overflow-y-auto rounded-lg border px-4 py-3 text-xs leading-5"
          >
            {rowErrors.map(({ rowNumber, problems }) => (
              <li key={rowNumber} className="break-words">
                <span className="font-semibold">Row {rowNumber}:</span> {problems.join('; ')}.
              </li>
            ))}
          </ul>
        )}
      </Dialog>

      <DeleteConfirmationDialog id={BULK_DELETE_MATTERS_CONFIRM} title="Delete these matters?" confirmLabel="Delete" />
    </>
  );
};
